import merge from "lodash/merge";
import { useEffect, useMemo, useState } from "react";
import { DEFAULT_HIGHCHARTS_SPLINE_CHART_OPTIONS } from "@constants/highcharts";
import { resources } from "@constants/hardcoded-strings";
import { HighchartsChartContainer } from "../../../common/highcharts-chart-container/highcharts-chart-container";
import { DashboardVerificationCount } from "@idw-types/data/dashboard";
import format from "date-fns/format";
import {
    DISPLAY_DATE,
    DISPLAY_HOUR_MINUTE,
    SERVER_DATE_FORMAT,
    SERVER_DATE_HOUR_FORMAT
} from "@constants/date-formats";
import { TimeRangeTypes } from "@enums/time-range-types";
import differenceInDays from "date-fns/differenceInDays";
import parse from "date-fns/parse";
import {
    APPROVED_COLOR,
    NO_DECISION_COLOR,
    DENIED_COLOR,
    OBLIGATION_COLOR
} from "@constants/colors";

type DashboardVerificationCountsSplineChartProps = {
    verificationCounts: Array<DashboardVerificationCount>;
    timeRangeType: TimeRangeTypes;
    startDate: Date;
    endDate: Date;
};


export const DashboardVerificationCountsSplineChart = (props: DashboardVerificationCountsSplineChartProps) => {
    const { verificationCounts, timeRangeType, startDate, endDate } = props;

    const dateFormat = useMemo(() => {
        let finalDateFormat = DISPLAY_DATE;
        if (timeRangeType === TimeRangeTypes.PAST_TWENTY_FOUR_HOURS) {
            finalDateFormat = DISPLAY_HOUR_MINUTE;
        } else if (timeRangeType === TimeRangeTypes.CUSTOM) {
            const duration = differenceInDays(endDate, startDate);
            if (duration < 2) {
                finalDateFormat = DISPLAY_HOUR_MINUTE;
            }
        }
        return finalDateFormat;
    }, [timeRangeType, startDate, endDate]);

    const data = useMemo(() => {
        const approvedData: Array<number> = [];
        const deniedData: Array<number> = [];
        const obligationData: Array<number> = [];
        const noDecisionData: Array<number> = [];

        const dates: Array<string> = verificationCounts.map(count => count.date);
        const initialFormat = dateFormat === DISPLAY_DATE ? SERVER_DATE_FORMAT : SERVER_DATE_HOUR_FORMAT;

        const categories = dates.map(date => {
            return parse(date, initialFormat, new Date());
        });
        dates.forEach(date => {
            const count = verificationCounts.find(count => count.date === date);
            approvedData.push(count.APPROVED);
            deniedData.push(count.DENIED);
            obligationData.push(count.OBLIGATION);
            noDecisionData.push(count["NO DECISION"] - count.OBLIGATION);
        });

        const series: Array<{ name: string; data: Array<number>; color: string; }> = [
            {
                name: resources.charts.dashboardVerificationCounts.labels.approved,
                data: approvedData,
                color: APPROVED_COLOR
            },
            {
                name: resources.charts.dashboardVerificationCounts.labels.denied,
                data: deniedData,
                color: DENIED_COLOR
            },
            {
                name: resources.charts.dashboardVerificationCounts.labels.obligations,
                data: obligationData,
                color: OBLIGATION_COLOR
            },
            {
                name: resources.charts.dashboardVerificationCounts.labels.noDecision,
                data: noDecisionData,
                color: NO_DECISION_COLOR
            }
        ];
        return { series, categories };
    }, [verificationCounts, dateFormat]);

    const [chartOptions, setChartOptions] = useState<Highcharts.Options>(merge({}, DEFAULT_HIGHCHARTS_SPLINE_CHART_OPTIONS, {
        chart: {
            height: "400"
        },
        xAxis: {
            title: {
                enabled: false
            },
            categories: data.categories,
            type: "datetime",
            labels: {
                formatter: function () {
                    return format(this.value, dateFormat)
                }
            }
        },
        yAxis: {
            title: {
                enabled: false
            }
        },
        tooltip: {
            split: true,
            formatter: function () {
                const points = this.points;
                const xAxisTooltipText = format(this.x, dateFormat);
                const tooltipArray = [xAxisTooltipText];

                points.forEach((point: any) => {
                    tooltipArray.push(`<span>${point.series.name}</span>: ${point.y}<br/>`);
                });

                return tooltipArray;
            }
        },
        legend: {
            title: {
                text: resources.charts.dashboardVerificationCounts.legend.title
            },
            useHTML: true
        },
        series: data.series,
    }));

    useEffect(() => {
        const newChartOptions: Highcharts.Options = merge({}, chartOptions, {
            series: data.series,
            xAxis: {
                title: {
                    text: dateFormat === DISPLAY_DATE
                        ? resources.charts.dashboardVerificationCounts.xAxis.titles.day
                        : resources.charts.dashboardVerificationCounts.xAxis.titles.hour
                },
                categories: data.categories
            }
        });

        setChartOptions(newChartOptions);

    }, [data]);

    return <HighchartsChartContainer options={chartOptions} />;
};